import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { _T, C } from '../../shared/theme';
import Avatar from '../../components/ui/Avatar';
import StatusDot from '../../components/ui/StatusDot';
import { sendChatMessage, deleteChatMessage, fetchConversations, fetchUsers, markChatRead, saveChatReaction } from '../../shared/dbService';
import { playSound } from '../../shared/soundEngine';
import { timeAgo } from '../../shared/utils';

// ── Emoji picker data ─────────────────────────────────────────────────────────
const EMOJI_GROUPS = [
  { label: '😊', emojis: ['😀','😂','😍','🥰','😎','🤩','😅','😭','😤','🤔','🥳','😴','😇','🤗','😏','😬','🙄','😳','🥺','😈'] },
  { label: '👍', emojis: ['👍','👎','👏','🙌','🤝','🙏','💪','🤜','🤛','✌️','👌','🤌','🖐️','👋','🤙','💅','🫶','❤️','🔥','✨'] },
  { label: '🏋️', emojis: ['🏋️','🧘','🤸','🏃','🚴','⚽','🏀','🎯','🏆','🥇','💪','🧠','💤','🫁','❤️‍🔥','🌟','⚡','🌀','🎉','🎊'] },
  { label: '🌙', emojis: ['🌙','☀️','🌈','⭐','🌊','🔥','❄️','🌸','🌿','🍀','🦋','🐉','🌌','🚀','💫','🪐','🌕','🌊','🎆','🎇'] },
];

// ── Inline CSS animations ─────────────────────────────────────────────────────
const CHAT_CSS = `
  @keyframes slideInRight { from { opacity:0; transform:translateX(40px) scale(0.92); } to { opacity:1; transform:translateX(0) scale(1); } }
  @keyframes slideInLeft  { from { opacity:0; transform:translateX(-40px) scale(0.92); } to { opacity:1; transform:translateX(0) scale(1); } }
  @keyframes popIn        { from { opacity:0; transform:scale(0.6); } to { opacity:1; transform:scale(1); } }
  @keyframes floatUp      { 0%{opacity:1;transform:translateY(0) scale(1)} 100%{opacity:0;transform:translateY(-60px) scale(1.6)} }
  @keyframes shimmer      { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
  @keyframes pulse3d      { 0%,100%{box-shadow:0 0 0 0 rgba(200,168,75,0.4)} 50%{box-shadow:0 0 0 10px rgba(200,168,75,0)} }
  @keyframes typingDot    { 0%,80%,100%{transform:scale(0.6);opacity:0.4} 40%{transform:scale(1);opacity:1} }
  @keyframes emojiPop     { from{opacity:0;transform:translateY(10px) scale(0.8)} to{opacity:1;transform:translateY(0) scale(1)} }
  @keyframes reactionPop  { from{opacity:0;transform:scale(0)} 70%{transform:scale(1.3)} to{opacity:1;transform:scale(1)} }
  @keyframes sendBurst    { 0%{transform:scale(1)} 30%{transform:scale(1.3)} 100%{transform:scale(1)} }
  @keyframes recPulse     { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.5;transform:scale(1.3)} }
  @keyframes uploadSpin   { to{transform:rotate(360deg)} }
  @keyframes attachPop    { from{opacity:0;transform:translateY(10px) scale(0.95)} to{opacity:1;transform:translateY(0) scale(1)} }
  .msg-me   { animation: slideInRight 0.28s cubic-bezier(0.34,1.56,0.64,1); user-select:none; -webkit-user-select:none; }
  .msg-them { animation: slideInLeft  0.28s cubic-bezier(0.34,1.56,0.64,1); user-select:none; -webkit-user-select:none; }
  .emoji-pop { animation: emojiPop 0.2s ease; }
  .reaction-pop { animation: reactionPop 0.3s cubic-bezier(0.34,1.56,0.64,1); }
  .send-burst { animation: sendBurst 0.25s ease; }
  .attach-pop { animation: attachPop 0.18s ease; }
  ::-webkit-scrollbar { width: 3px; }
  ::-webkit-scrollbar-thumb { background: rgba(200,168,75,0.3); border-radius:4px; }
`;

const QUICK_REACTIONS = ['❤️','🔥','😂','👍','😮','💪'];

// ── Link-aware text renderer ──────────────────────────────────────────────────
const URL_RE = /https?:\/\/[^\s<>"']+/g;
function renderText(text) {
  if (!text) return null;
  const parts = [];
  let last = 0, match;
  URL_RE.lastIndex = 0;
  while ((match = URL_RE.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    const url = match[0].replace(/[.,!?)]+$/, ''); // strip trailing punctuation
    parts.push(
      <a key={match.index} href={url} target="_blank" rel="noopener noreferrer"
        style={{ color:'#5B9ED0', textDecoration:'underline', wordBreak:'break-all' }}
        onClick={e => e.stopPropagation()}>
        {url}
      </a>
    );
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length ? parts : text;
}

// ── Voice bubble ──────────────────────────────────────────────────────────────
function VoiceBubble({ audio, duration, isMe }) {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef(null);
  const sec = Math.floor(duration || 0);
  const label = `${Math.floor(sec/60)}:${String(sec%60).padStart(2,'0')}`;

  const play = () => {
    if (playing && audioRef.current) { audioRef.current.pause(); setPlaying(false); return; }
    const a = new Audio(audio);
    audioRef.current = a;
    a.onplay  = () => setPlaying(true);
    a.onended = () => { setPlaying(false); setProgress(0); };
    a.ontimeupdate = () => setProgress(a.currentTime / (a.duration || 1));
    a.play().catch(() => {});
  };

  return (
    <div onClick={play} style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer', minWidth:160 }}>
      <div style={{ width:34, height:34, borderRadius:'50%', background: isMe ? 'rgba(255,255,255,0.15)' : 'rgba(200,168,75,0.2)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, flexShrink:0 }}>
        {playing ? '⏸' : '▶'}
      </div>
      <div style={{ flex:1 }}>
        <div style={{ position:'relative', height:4, background:'rgba(255,255,255,0.12)', borderRadius:4, overflow:'hidden', marginBottom:4 }}>
          <div style={{ position:'absolute', left:0, top:0, height:'100%', width:`${progress*100}%`, background: isMe ? 'rgba(255,255,255,0.8)' : _T.accent, borderRadius:4, transition:'width 0.25s linear' }} />
          {/* Waveform dots */}
          {[...Array(20)].map((_,i) => (
            <div key={i} style={{ position:'absolute', left:`${i*5+1}%`, bottom:0, width:2, height:`${30+Math.sin(i)*50}%`, background:'rgba(255,255,255,0.15)', borderRadius:1 }} />
          ))}
        </div>
        <div style={{ fontSize:10, color: isMe ? 'rgba(255,255,255,0.6)' : C.silver }}>🎤 {label}</div>
      </div>
    </div>
  );
}

// ── Video message bubble ──────────────────────────────────────────────────────
function VideoMessage({ url, filename }) {
  return (
    <div style={{ position:'relative' }}>
      <video
        src={url}
        controls
        playsInline
        style={{ maxWidth:240, maxHeight:220, borderRadius:12, display:'block', background:'#000', outline:'none' }}
      />
      {filename && (
        <div style={{ fontSize:10, color:'rgba(255,255,255,0.45)', marginTop:4, maxWidth:240, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
          🎬 {filename}
        </div>
      )}
    </div>
  );
}

// ── Document bubble ───────────────────────────────────────────────────────────
function DocumentBubble({ url, filename, size, mime, isMe }) {
  const ext = (filename || '').split('.').pop().toLowerCase();
  const icon = mime?.includes('pdf') ? '📄'
    : mime?.includes('word') || ext === 'doc' || ext === 'docx' ? '📝'
    : mime?.includes('sheet') || mime?.includes('excel') || ext === 'xls' || ext === 'xlsx' ? '📊'
    : mime?.includes('presentation') || ext === 'ppt' || ext === 'pptx' ? '📽'
    : ext === 'txt' || ext === 'csv' ? '📃'
    : '📎';
  const sizeLabel = size
    ? (size > 1024 * 1024 ? (size / 1024 / 1024).toFixed(1) + ' MB' : Math.ceil(size / 1024) + ' KB')
    : '';
  return (
    <a
      href={url} target="_blank" rel="noopener noreferrer"
      style={{ display:'flex', alignItems:'center', gap:10, textDecoration:'none', minWidth:180, maxWidth:240 }}
    >
      <div style={{
        width:42, height:42, borderRadius:10, flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center',
        background: isMe ? 'rgba(255,255,255,0.1)' : 'rgba(200,168,75,0.15)',
        fontSize:22,
      }}>{icon}</div>
      <div style={{ flex:1, overflow:'hidden' }}>
        <div style={{ color:'#EEF3FF', fontSize:13, fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
          {filename || 'Document'}
        </div>
        <div style={{ fontSize:10, color:'rgba(255,255,255,0.45)', marginTop:2 }}>
          {sizeLabel}{sizeLabel ? ' · ' : ''}Tap to open
        </div>
      </div>
    </a>
  );
}

// ── Floating emoji burst ──────────────────────────────────────────────────────
function EmojiBurst({ emoji, onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 900); return () => clearTimeout(t); }, [onDone]);
  return (
    <div style={{ position:'fixed', bottom:120, left:'50%', transform:'translateX(-50%)', fontSize:36, pointerEvents:'none', zIndex:999, animation:'floatUp 0.9s ease forwards' }}>
      {emoji}
    </div>
  );
}

// ── Date separator helpers ────────────────────────────────────────────────────
function getMsgDayKey(msgId) {
  if (!msgId) return '';
  const d = new Date(typeof msgId === 'string' ? parseInt(msgId, 10) : msgId);
  if (isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function getDayLabel(msgId, lang) {
  if (!msgId) return null;
  const d = new Date(typeof msgId === 'string' ? parseInt(msgId, 10) : msgId);
  if (isNaN(d.getTime())) return null;
  const nl = lang === 'nl';
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const msgDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.floor((today - msgDay) / 86400000);
  if (diffDays === 0) return nl ? 'Vandaag' : 'Today';
  if (diffDays === 1) return nl ? 'Gisteren' : 'Yesterday';
  if (diffDays < 7) return nl ? `${diffDays} dagen geleden` : `${diffDays} days ago`;
  return d.toLocaleDateString(nl ? 'nl-NL' : 'en-US', { weekday: 'long', month: 'short', day: 'numeric' });
}

// ── Main ChatScreen ───────────────────────────────────────────────────────────
export default function ChatScreen({ lang, currentUser, allUsers, friends, conversations, setConversations, onOpenProfile }) {
  const nl = lang === 'nl';
  const [activeChat, setActiveChat]       = useState(null);
  const [friendSearch, setFriendSearch]   = useState('');
  const [input, setInput]                 = useState('');
  const [showEmoji, setShowEmoji]         = useState(false);
  const [emojiGroup, setEmojiGroup]       = useState(0);
  const [burst, setBurst]                 = useState(null);
  const [reactions, setReactions]         = useState(() => { try { return JSON.parse(localStorage.getItem('sl_chat_reactions') || '{}'); } catch { return {}; } });
  const [longPressMsg, setLongPressMsg]   = useState(null);
  const [lightboxImg, setLightboxImg]     = useState(null);
  const [imagePreview, setImagePreview]   = useState(null);
  const [sendAnim, setSendAnim]           = useState(false);
  const [dbUsers, setDbUsers]             = useState([]);
  const [imgLoading, setImgLoading]       = useState(false);
  const [usersLoading, setUsersLoading]   = useState(true);
  const [showAttach, setShowAttach]       = useState(false);
  const [isRecording, setIsRecording]     = useState(false);
  const [inputFocused, setInputFocused]   = useState(false);
  const [vpHeight, setVpHeight]           = useState(() => window.visualViewport?.height || window.innerHeight);
  const [partnerTyping, setPartnerTyping] = useState(false);
  const typingTimerRef                    = useRef(null);
  const pollRef                           = useRef(null);
  const [recDuration, setRecDuration]     = useState(0);
  const [fileUploading, setFileUploading] = useState(false);
  const bottomRef      = useRef(null);
  const msgsContainerRef = useRef(null);
  const inputRef       = useRef(null);
  const fileRef        = useRef(null);
  const videoFileRef   = useRef(null);
  const docFileRef     = useRef(null);
  const audioFileRef   = useRef(null);
  const lpTimer        = useRef(null);
  const hasLoadedOnce  = useRef(false);   // spinner only on first mount
  const mediaRecRef    = useRef(null);
  const chunksRef      = useRef([]);
  const recTimerRef    = useRef(null);
  const recDurRef      = useRef(0);
  const BASE_API       = process.env.REACT_APP_API_URL || '/api';

  // Track visual viewport height so balloon floats above OS keyboard
  useEffect(() => {
    const vp = window.visualViewport;
    if (!vp) return;
    const onResize = () => setVpHeight(vp.height);
    vp.addEventListener('resize', onResize);
    return () => vp.removeEventListener('resize', onResize);
  }, []);

  // Poll for new messages + read receipts + typing when a chat is open
  useEffect(() => {
    if (!activeChat) { clearInterval(pollRef.current); return; }
    const poll = async () => {
      try {
        const res = await fetch(`${BASE_API}/chat/poll?uid=${encodeURIComponent(currentUser.id)}&partnerId=${encodeURIComponent(activeChat.id)}`);
        if (!res.ok) return;
        const data = await res.json();
        if (Array.isArray(data.messages)) {
          const key = [currentUser.id, activeChat.id].sort().join('_');
          setConversations(prev => {
            // Full replace from DB for this conversation + preserve any pending temp messages
            const local = prev[key] || [];
            const dbIds = new Set(data.messages.map(m => m.id));
            const pending = local.filter(m => !dbIds.has(m.id) && typeof m.id === 'number' && m.id > 1e12 && m.id < 1e15);
            const merged = [...data.messages, ...pending].sort((a,b) => (a.id||0)-(b.id||0));
            return { ...prev, [key]: merged };
          });
        }
        // Merge DB reactions (keyed by msgId → { userId: emoji }), extract current user's reactions
        if (data.reactions && typeof data.reactions === 'object') {
          const myId = currentUser.id;
          const myReactions = {};
          Object.entries(data.reactions).forEach(([msgId, userMap]) => {
            if (userMap && userMap[myId]) myReactions[msgId] = userMap[myId];
          });
          if (Object.keys(myReactions).length > 0) {
            setReactions(prev => ({ ...prev, ...myReactions }));
          }
        }
        setPartnerTyping(!!data.typing);
      } catch {}
    };
    poll();
    pollRef.current = setInterval(poll, 3000);
    return () => clearInterval(pollRef.current);
  }, [activeChat, currentUser.id, BASE_API]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch friend profiles.
  // First load: shows spinner. Subsequent calls (from sync updating friends prop)
  // refresh dbUsers silently in the background — no spinner flash.
  useEffect(() => {
    if (!friends || friends.length === 0) { setUsersLoading(false); hasLoadedOnce.current = true; return; }
    if (!hasLoadedOnce.current) setUsersLoading(true);   // spinner only on first load
    fetchUsers(currentUser.id)
      .then(users => { if (Array.isArray(users)) setDbUsers(users.filter(u => friends.includes(u.id))); })
      .finally(() => { setUsersLoading(false); hasLoadedOnce.current = true; });
  }, [friends, currentUser.id]);

  // Fetch conversations from DB — full replace (DB is source of truth)
  useEffect(() => {
    fetchConversations().then(dbConvos => {
      if (!dbConvos || typeof dbConvos !== 'object') return;
      setConversations(prev => ({ ...prev, ...dbConvos }));
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Only build friendUsers from resolved DB data — never fall back to UUID fragments
  const friendUsers = usersLoading ? [] : friends.map(fid =>
    dbUsers.find(u => u.id === fid) ||
    (allUsers||[]).find(u => u.id === fid) ||
    null
  ).filter(Boolean);

  const openChat = (u) => {
    setActiveChat(u);
    // Mark partner's messages as read immediately
    markChatRead(currentUser.id, u.id);
    // Also update local state so unread badge clears instantly
    const key = [currentUser.id, u.id].sort().join('_');
    setConversations(prev => ({
      ...prev,
      [key]: (prev[key] || []).map(m => m.from !== currentUser.id ? { ...m, read: true } : m),
    }));
  };

  // Scroll to top (newest messages) when chat opens
  useEffect(() => {
    if (msgsContainerRef.current) msgsContainerRef.current.scrollTop = 0;
  }, [activeChat]);

  // ── Typing signal ─────────────────────────────────────────────────────────
  const sendTyping = useCallback((isTyping) => {
    if (!activeChat) return;
    fetch(`${BASE_API}/chat/typing`, { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ uid: currentUser.id, partnerId: activeChat.id, isTyping: !!isTyping }) }).catch(() => {});
  }, [activeChat, currentUser.id, BASE_API]);

  const onInputChange = useCallback((e) => {
    setInput(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
    sendTyping(true);
    clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => sendTyping(false), 5000);
  }, [sendTyping]);

  // ── Send text message ─────────────────────────────────────────────────────
  const sendMsg = useCallback(async (text, type = 'text', extra = {}) => {
    if (!activeChat) return;
    if (type === 'text' && !text.trim()) return;
    const key = [currentUser.id, activeChat.id].sort().join('_');
    const time = new Date().toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' });
    const tempId = Date.now();
    const msg = { id: tempId, from: currentUser.id, text: text.trim(), time, type, ...extra };

    playSound('message_sent');
    setSendAnim(true);
    setTimeout(() => setSendAnim(false), 300);
    setConversations(prev => ({ ...prev, [key]: [...(prev[key]||[]), msg] }));
    // Scroll to top so newest message (our just-sent one) is visible
    setTimeout(() => { if (msgsContainerRef.current) msgsContainerRef.current.scrollTop = 0; }, 30);
    setInput('');
    setImagePreview(null);
    setShowEmoji(false);
    clearTimeout(typingTimerRef.current);
    sendTyping(false);

    const { audio: _a, duration: _d, img: _i, ...restExtra } = extra;
    const result = await sendChatMessage(currentUser.id, activeChat.id, text.trim(), time, type, extra.audio || null, extra.duration || null, extra.img || null, restExtra);
    if (result?.msg?.id && result.msg.id !== tempId) {
      setConversations(prev => ({
        ...prev,
        [key]: (prev[key]||[]).map(m => m.id === tempId ? { ...m, id: result.msg.id } : m),
      }));
    }
  }, [activeChat, currentUser.id, setConversations]);

  // ── Send image ────────────────────────────────────────────────────────────
  const handleImageFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImgLoading(true);
    const reader = new FileReader();
    reader.onload = (ev) => {
      setImagePreview(ev.target.result);
      setImgLoading(false);
    };
    reader.readAsDataURL(file);
  };

  const sendImage = async () => {
    if (!imagePreview) return;
    await sendMsg('', 'image', { img: imagePreview });
  };

  // ── Upload a file to /api/upload, returns server URL ──────────────────────
  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${BASE_API}/upload`, { method: 'POST', body: fd });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Upload failed');
    }
    return await res.json(); // { url, mime, size, name }
  };

  // ── Send video ────────────────────────────────────────────────────────────
  const handleVideoFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setFileUploading(true);
    setShowAttach(false);
    try {
      const { url, mime, size } = await uploadFile(file);
      await sendMsg('', 'video', { url, filename: file.name, size, mime });
    } catch (err) { alert(err.message || 'Upload failed'); }
    finally { setFileUploading(false); }
  };

  // ── Send document ─────────────────────────────────────────────────────────
  const handleDocFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setFileUploading(true);
    setShowAttach(false);
    try {
      const { url, mime, size } = await uploadFile(file);
      await sendMsg('', 'document', { url, filename: file.name, size, mime });
    } catch (err) { alert(err.message || 'Upload failed'); }
    finally { setFileUploading(false); }
  };

  // ── Send audio file ───────────────────────────────────────────────────────
  const handleAudioFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setFileUploading(true);
    setShowAttach(false);
    try {
      const { url, mime, size } = await uploadFile(file);
      await sendMsg('', 'audiofile', { url, filename: file.name, size, mime });
    } catch (err) { alert(err.message || 'Upload failed'); }
    finally { setFileUploading(false); }
  };

  // ── Voice recording ───────────────────────────────────────────────────────
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/ogg' });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: mr.mimeType });
        const reader = new FileReader();
        reader.onload = (ev) => {
          sendMsg('', 'voice', { audio: ev.target.result, duration: recDurRef.current });
          setRecDuration(0);
          recDurRef.current = 0;
        };
        reader.readAsDataURL(blob);
        setIsRecording(false);
      };
      mr.start();
      mediaRecRef.current = mr;
      recDurRef.current = 0;
      setRecDuration(0);
      setIsRecording(true);
      recTimerRef.current = setInterval(() => {
        recDurRef.current += 1;
        setRecDuration(d => d + 1);
      }, 1000);
    } catch {
      alert('Microphone access denied');
      setIsRecording(false);
    }
  };

  const stopRecording = (cancel = false) => {
    clearInterval(recTimerRef.current);
    if (!mediaRecRef.current || mediaRecRef.current.state === 'inactive') {
      setIsRecording(false);
      return;
    }
    if (cancel) {
      // Discard chunks — replace onstop with a no-op
      mediaRecRef.current.onstop = () => {
        mediaRecRef.current.stream?.getTracks().forEach(t => t.stop());
        setIsRecording(false);
        setRecDuration(0);
        recDurRef.current = 0;
      };
    }
    mediaRecRef.current.stop();
  };

  // ── Emoji insert ──────────────────────────────────────────────────────────
  const insertEmoji = (em) => {
    setInput(prev => prev + em);
    setBurst(em);
    inputRef.current?.focus();
  };

  // ── Reaction on long-press ────────────────────────────────────────────────
  const onMsgPressStart = (msg) => {
    lpTimer.current = setTimeout(() => setLongPressMsg(msg), 1000);
  };
  const onMsgPressEnd = () => { clearTimeout(lpTimer.current); };

  const addReaction = (msgId, emoji) => {
    setReactions(prev => {
      const next = { ...prev, [msgId]: emoji };
      try { localStorage.setItem('sl_chat_reactions', JSON.stringify(next)); } catch {}
      return next;
    });
    // Persist to DB
    if (activeChat) saveChatReaction(currentUser.id, activeChat.id, msgId, emoji);
    setLongPressMsg(null);
    setBurst(emoji);
  };

  // ── Delete message ────────────────────────────────────────────────────────
  const deleteMsg = (msg) => {
    if (msg.from !== currentUser.id) return;
    const key = [currentUser.id, activeChat.id].sort().join('_');
    setConversations(prev => ({ ...prev, [key]: (prev[key]||[]).filter(m => m.id !== msg.id) }));
    deleteChatMessage(currentUser.id, activeChat.id, msg.id);
    setLongPressMsg(null);
  };

  // ── Loading gate — all hooks above, safe to early-return from here ──────────
  if (usersLoading) {
    return (
      <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:'40vh', gap:16 }}>
        <style>{CHAT_CSS}{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <div style={{ width:40, height:40, borderRadius:'50%', border:`3px solid ${_T.cardBorderBot}`, borderTopColor:_T.accent, animation:'spin 0.85s linear infinite' }} />
        <div style={{ color:C.silver, fontSize:13 }}>{nl ? 'Berichten laden...' : 'Loading messages...'}</div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // ACTIVE CHAT VIEW
  // ─────────────────────────────────────────────────────────────────────────
  if (activeChat) {
    const key = [currentUser.id, activeChat.id].sort().join('_');
    const msgs = conversations[key] || [];
    const accentHex = _T.accent;

    return ReactDOM.createPortal(
      <div style={{ display:'flex', flexDirection:'column', position:'fixed', top:'calc(63px + env(safe-area-inset-top))', left:0, right:0, bottom:'calc(77px + env(safe-area-inset-bottom))', zIndex:1000, background:_T.bg, overflow:'hidden', borderRadius:'16px 16px 0 0' }}>
        <style>{CHAT_CSS}</style>

        {/* Burst emoji effect */}
        {burst && <EmojiBurst emoji={burst} onDone={() => setBurst(null)} />}

        {/* 3D Glass header */}
        <div style={{
          display:'flex', alignItems:'center', gap:12,
          padding:'14px 12px',
          background:`linear-gradient(135deg, ${_T.bg3} 0%, ${_T.bg4} 100%)`,
          borderBottom:`1px solid ${_T.cardBorderTop}`,
          backdropFilter:'blur(20px)',
          boxShadow:`0 4px 20px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.06)`,
          borderRadius:'16px 16px 0 0',
          flexShrink:0,
        }}>
          <button onClick={() => setActiveChat(null)} style={{ background:'none', border:'none', color:C.silver, cursor:'pointer', fontSize:20, padding:'4px 8px', borderRadius:10, lineHeight:1 }}>←</button>
          <div style={{ position:'relative', cursor:'pointer' }} onClick={() => onOpenProfile(activeChat)}>
            <Avatar user={activeChat} size={40} profileImage={activeChat.picture||null} />
            <span style={{ position:'absolute', bottom:1, right:1 }}><StatusDot status={activeChat.status||'offline'} size={11} /></span>
          </div>
          <div style={{ flex:1 }}>
            <div style={{ color:'#EEF3FF', fontSize:15, fontWeight:700 }}>{activeChat.name}</div>
            <div style={{ fontSize:11, color: activeChat.status==='online' ? '#4AC87A' : C.silver }}>
              {activeChat.status === 'online' ? '● Online' : activeChat.status === 'resting' ? '🌿 Resting' : '○ Offline'}
            </div>
          </div>
          <button
            onClick={() => onOpenProfile(activeChat)}
            style={{ background:'none', border:`1px solid ${_T.cardBorderBot}`, borderRadius:10, color:C.silver, fontSize:11, padding:'5px 10px', cursor:'pointer' }}
          >
            {nl ? 'Profiel' : 'Profile'}
          </button>
        </div>

        {/* Messages list — newest at top, scroll down for history */}
        <div
          ref={msgsContainerRef}
          style={{
            flex:1, overflowY:'auto', padding:'12px 12px 8px',
            background:`radial-gradient(ellipse at 20% 80%, ${accentHex}08 0%, transparent 60%), radial-gradient(ellipse at 80% 20%, ${_T.bg2}80 0%, transparent 60%)`,
            display:'flex', flexDirection:'column-reverse', gap:6,
            overflowAnchor:'none',
          }}
        >
          {/* Typing indicator — shown at top (newest end) */}
          {partnerTyping && (
            <div className="msg-them" style={{ display:'flex', flexDirection:'row', gap:8, alignItems:'flex-end', padding:'0 2px' }}>
              <div style={{ width:30, flexShrink:0, alignSelf:'flex-end', marginBottom:2 }}>
                <Avatar user={activeChat} size={28} profileImage={activeChat.picture||null} />
              </div>
              <div style={{ background:`linear-gradient(135deg, ${_T.bg3} 0%, ${_T.bg4} 100%)`, border:`1px solid ${_T.cardBorderBot}`, borderRadius:'18px 18px 18px 4px', padding:'12px 16px', display:'flex', gap:4, alignItems:'center' }}>
                {[0,1,2].map(i => (
                  <div key={i} style={{ width:7, height:7, borderRadius:'50%', background:C.silver, animation:`typingDot 1.2s ease-in-out ${i*0.2}s infinite` }} />
                ))}
              </div>
            </div>
          )}

          {msgs.length === 0 && (
            <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', color:C.silver, fontSize:13, gap:10, padding:'40px 0' }}>
              <div style={{ fontSize:48 }}>✉️</div>
              <div>{nl ? 'Stuur een bericht...' : 'Say something...'}</div>
            </div>
          )}

          {/* Render newest-first using column-reverse — separators inserted before day changes */}
          {[...msgs].reverse().map((m, ri) => {
            const i = msgs.length - 1 - ri; // original index
            const isMe = m.from === currentUser.id;
            const reaction = reactions[m.id];
            // Show day separator when day changes (comparing with next-older message)
            const olderMsg = msgs[i - 1];
            const showSeparator = !olderMsg || getMsgDayKey(m.id) !== getMsgDayKey(olderMsg.id);
            const dayLabel = showSeparator ? getDayLabel(m.id, lang) : null;
            return (
              <React.Fragment key={m.id}>
                <div
                  className={isMe ? 'msg-me' : 'msg-them'}
                  style={{ display:'flex', flexDirection: isMe ? 'row-reverse' : 'row', gap:8, alignItems:'flex-end', padding:'0 2px' }}
                  onMouseDown={() => onMsgPressStart(m)}
                  onMouseUp={onMsgPressEnd}
                  onTouchStart={() => onMsgPressStart(m)}
                  onTouchEnd={onMsgPressEnd}
                  onContextMenu={e => e.preventDefault()}
                >
                  {/* Avatar — shown on every "them" message */}
                  {!isMe && (
                    <div style={{ width:32, flexShrink:0, alignSelf:'flex-end', marginBottom:2 }}>
                      <Avatar user={activeChat} size={30} profileImage={activeChat.picture||null} />
                    </div>
                  )}

                  <div style={{ maxWidth:'72%', position:'relative' }}>
                    {/* Bubble */}
                    <div style={{
                      background: isMe
                        ? `linear-gradient(135deg, ${accentHex}35 0%, ${accentHex}20 100%)`
                        : `linear-gradient(135deg, ${_T.bg3} 0%, ${_T.bg4} 100%)`,
                      border: `1px solid ${isMe ? accentHex+'55' : _T.cardBorderBot}`,
                      borderRadius: isMe ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                      padding: (m.type === 'image' || m.type === 'video') ? '4px' : '10px 14px',
                      backdropFilter:'blur(10px)',
                      boxShadow: isMe
                        ? `0 4px 16px ${accentHex}22, inset 0 1px 0 rgba(255,255,255,0.08)`
                        : `0 4px 16px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.04)`,
                      position:'relative',
                      overflow:'hidden',
                    }}>
                      {isMe && (
                        <div style={{ position:'absolute', inset:0, background:`linear-gradient(90deg,transparent,rgba(255,255,255,0.04),transparent)`, backgroundSize:'200% 100%', animation:'shimmer 3s infinite', pointerEvents:'none' }} />
                      )}

                      {m.type === 'voice' && m.audio ? (
                        <VoiceBubble audio={m.audio} duration={m.duration} isMe={isMe} />
                      ) : m.type === 'audiofile' && m.url ? (
                        <VoiceBubble audio={m.url} duration={m.duration} isMe={isMe} />
                      ) : m.type === 'image' && m.img ? (
                        <img src={m.img} alt="" onClick={() => setLightboxImg(m.img)} style={{ maxWidth:220, maxHeight:220, borderRadius:14, display:'block', cursor:'zoom-in' }} />
                      ) : m.type === 'video' && m.url ? (
                        <VideoMessage url={m.url} filename={m.filename} isMe={isMe} />
                      ) : m.type === 'document' && m.url ? (
                        <DocumentBubble url={m.url} filename={m.filename} size={m.size} mime={m.mime} isMe={isMe} />
                      ) : (
                        <div style={{ color: isMe ? '#EEF3FF' : C.silver2, fontSize:14, lineHeight:1.55, wordBreak:'break-word' }}>
                          {renderText(m.text)}
                        </div>
                      )}

                      {/* Timestamp + read receipt + time ago */}
                      <div style={{ fontSize:10, color: isMe ? 'rgba(255,255,255,0.4)' : C.silver, marginTop: (m.type==='image'||m.type==='video') ? 4 : 3, textAlign: isMe ? 'right' : 'left', padding: (m.type==='image'||m.type==='video') ? '0 6px 4px' : 0, display:'flex', alignItems:'center', justifyContent: isMe ? 'flex-end' : 'flex-start', gap:4 }}>
                        <span>{m.time}</span>
                        <span style={{ opacity:0.6 }}>· {timeAgo(m.id, lang)}</span>
                        {isMe && (
                          <span style={{
                            color: m.read ? '#4AC87A' : m.delivered ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.25)',
                            fontSize:11, letterSpacing:-1, fontWeight:700
                          }} title={m.read ? (nl ? 'Gelezen' : 'Read') : m.delivered ? (nl ? 'Afgeleverd' : 'Delivered') : (nl ? 'Verzonden' : 'Sent')}>
                            {(m.read || m.delivered) ? '✓✓' : '✓'}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Reaction badge */}
                    {reaction && (
                      <div className="reaction-pop" style={{ position:'absolute', bottom:-8, right: isMe ? 4 : 'auto', left: isMe ? 'auto' : 4, background:_T.bg3, border:`1px solid ${_T.cardBorderBot}`, borderRadius:12, padding:'1px 6px', fontSize:14 }}>
                        {reaction}
                      </div>
                    )}
                  </div>
                </div>

                {/* Date separator — appears below this day's oldest message (bottom of each day when scrolling) */}
                {dayLabel && (
                  <div style={{ display:'flex', alignItems:'center', gap:10, padding:'6px 0', flexShrink:0 }}>
                    <div style={{ flex:1, height:1, background:'rgba(255,255,255,0.07)' }} />
                    <div style={{ fontSize:11, color:C.silver, fontWeight:600, letterSpacing:0.5, padding:'2px 10px', background:_T.bg3, borderRadius:20, border:`1px solid rgba(255,255,255,0.08)` }}>
                      {dayLabel}
                    </div>
                    <div style={{ flex:1, height:1, background:'rgba(255,255,255,0.07)' }} />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Long-press reaction panel */}
        {longPressMsg && (
          <div
            style={{ position:'fixed', inset:0, zIndex:500, background:'rgba(0,0,0,0.55)', display:'flex', alignItems:'center', justifyContent:'center' }}
            onClick={() => setLongPressMsg(null)}
          >
            <div style={{ background:_T.bg3, border:`1px solid ${_T.cardBorderTop}`, borderRadius:20, padding:'14px 18px', display:'flex', flexDirection:'column', gap:12, boxShadow:'0 16px 48px rgba(0,0,0,0.6)' }} onClick={e=>e.stopPropagation()}>
              {/* Quick reactions */}
              <div style={{ display:'flex', gap:8 }}>
                {QUICK_REACTIONS.map(em => (
                  <button key={em} onClick={() => addReaction(longPressMsg.id, em)} style={{ background:'none', border:'none', fontSize:26, cursor:'pointer', padding:4, borderRadius:10, transition:'transform 0.15s' }}
                    onMouseEnter={e=>e.currentTarget.style.transform='scale(1.3)'}
                    onMouseLeave={e=>e.currentTarget.style.transform='scale(1)'}
                  >{em}</button>
                ))}
              </div>
              {/* Copy + Delete row */}
              <div style={{ display:'flex', gap:8 }}>
                {longPressMsg.text && (
                  <button onClick={() => { navigator.clipboard?.writeText(longPressMsg.text).catch(()=>{}); setLongPressMsg(null); }} style={{ flex:1, background:'rgba(255,255,255,0.06)', border:`1px solid ${_T.cardBorderBot}`, color:C.silver2, borderRadius:10, padding:'8px 14px', fontSize:13, cursor:'pointer' }}>
                    📋 {nl ? 'Kopiëren' : 'Copy'}
                  </button>
                )}
                {longPressMsg.from === currentUser.id && (
                  <button onClick={() => deleteMsg(longPressMsg)} style={{ flex:1, background:'rgba(192,80,80,0.15)', border:'1px solid rgba(192,80,80,0.3)', color:'#C05050', borderRadius:10, padding:'8px 14px', fontSize:13, cursor:'pointer' }}>
                    🗑 {nl ? 'Verwijderen' : 'Delete'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Upload progress bar */}
        {fileUploading && (
          <div style={{ padding:'8px 14px', background:_T.bg3, borderTop:`1px solid ${_T.cardBorderBot}`, display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ width:16, height:16, borderRadius:'50%', border:`2px solid ${accentHex}`, borderTopColor:'transparent', animation:'uploadSpin 0.7s linear infinite', flexShrink:0 }} />
            <div style={{ flex:1, color:C.silver, fontSize:12 }}>{nl ? 'Uploaden...' : 'Uploading...'}</div>
          </div>
        )}

        {/* Recording banner */}
        {isRecording && (
          <div style={{ padding:'10px 14px', background:_T.bg3, borderTop:`1px solid ${_T.cardBorderBot}`, display:'flex', alignItems:'center', gap:12 }}>
            <div style={{ fontSize:14, animation:'recPulse 1s infinite', color:'#ff4444' }}>●</div>
            <div style={{ color:'#ff6666', fontWeight:700, fontSize:13, flex:1 }}>
              {nl ? 'Opname ' : 'Recording '}{Math.floor(recDuration/60)}:{String(recDuration%60).padStart(2,'0')}
            </div>
            <button onClick={() => stopRecording(true)} style={{ background:'none', border:`1px solid rgba(192,80,80,0.4)`, borderRadius:10, color:'#ff6666', fontSize:12, padding:'6px 12px', cursor:'pointer' }}>
              ✕ {nl ? 'Annuleer' : 'Cancel'}
            </button>
            <button onClick={() => stopRecording(false)} style={{ background:`linear-gradient(135deg,${accentHex},${_T.grad2})`, border:'none', borderRadius:10, color:'#0a1220', fontWeight:700, fontSize:12, padding:'6px 14px', cursor:'pointer' }}>
              ⬛ {nl ? 'Stuur' : 'Send'}
            </button>
          </div>
        )}

        {/* Image preview strip */}
        {imagePreview && !isRecording && (
          <div style={{ padding:'8px 12px', background:_T.bg3, borderTop:`1px solid ${_T.cardBorderBot}`, display:'flex', alignItems:'center', gap:10 }}>
            <img src={imagePreview} alt="" style={{ height:60, borderRadius:8, border:`1px solid ${_T.cardBorderBot}` }} />
            <div style={{ flex:1, color:C.silver, fontSize:12 }}>{nl ? 'Afbeelding klaar om te verzenden' : 'Image ready to send'}</div>
            <button onClick={() => setImagePreview(null)} style={{ background:'none', border:'none', color:C.silver, fontSize:18, cursor:'pointer' }}>✕</button>
            <button onClick={sendImage} style={{ background:`linear-gradient(135deg,${accentHex},${_T.grad2})`, border:'none', borderRadius:12, color:'#0a1220', fontWeight:700, fontSize:13, padding:'8px 16px', cursor:'pointer' }}>
              {nl ? 'Stuur' : 'Send'}
            </button>
          </div>
        )}

        {/* Attach menu */}
        {showAttach && !isRecording && (
          <div className="attach-pop" style={{
            padding:'12px 14px', background:_T.bg3, borderTop:`1px solid ${_T.cardBorderBot}`,
            display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8,
          }}>
            {[
              { icon:'📷', labelNL:'Foto',     labelEN:'Photo',    action: () => { setShowAttach(false); fileRef.current?.click(); } },
              { icon:'🎬', labelNL:'Video',    labelEN:'Video',    action: () => { setShowAttach(false); videoFileRef.current?.click(); } },
              { icon:'📄', labelNL:'Document', labelEN:'Document', action: () => { setShowAttach(false); docFileRef.current?.click(); } },
              { icon:'🎵', labelNL:'Audio',    labelEN:'Audio',    action: () => { setShowAttach(false); audioFileRef.current?.click(); } },
            ].map(opt => (
              <button key={opt.icon} onClick={opt.action} style={{
                display:'flex', flexDirection:'column', alignItems:'center', gap:5,
                background:`linear-gradient(135deg, ${_T.bg4}, ${_T.bg3})`,
                border:`1px solid ${_T.cardBorderBot}`, borderRadius:14,
                padding:'12px 6px', cursor:'pointer', transition:'transform 0.15s',
              }}
                onMouseEnter={e=>e.currentTarget.style.transform='scale(1.06)'}
                onMouseLeave={e=>e.currentTarget.style.transform='scale(1)'}
              >
                <span style={{ fontSize:24 }}>{opt.icon}</span>
                <span style={{ fontSize:11, color:C.silver }}>{nl ? opt.labelNL : opt.labelEN}</span>
              </button>
            ))}
          </div>
        )}

        {/* Emoji picker */}
        {showEmoji && (
          <div className="emoji-pop" style={{
            background:_T.bg3, borderTop:`1px solid ${_T.cardBorderBot}`,
            backdropFilter:'blur(20px)', padding:'8px 8px 4px',
            boxShadow:'0 -8px 32px rgba(0,0,0,0.4)',
          }}>
            {/* Group tabs */}
            <div style={{ display:'flex', gap:4, marginBottom:8 }}>
              {EMOJI_GROUPS.map((g,i) => (
                <button key={i} onClick={() => setEmojiGroup(i)} style={{ background: emojiGroup===i ? `${accentHex}30` : 'none', border:`1px solid ${emojiGroup===i ? accentHex+'66' : 'transparent'}`, borderRadius:8, fontSize:16, padding:'4px 8px', cursor:'pointer' }}>
                  {g.label}
                </button>
              ))}
            </div>
            {/* Emoji grid */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(10,1fr)', gap:2 }}>
              {EMOJI_GROUPS[emojiGroup].emojis.map(em => (
                <button key={em} onClick={() => insertEmoji(em)} style={{ background:'none', border:'none', fontSize:22, padding:'4px 2px', cursor:'pointer', borderRadius:6, transition:'transform 0.1s' }}
                  onMouseEnter={e=>e.currentTarget.style.transform='scale(1.3)'}
                  onMouseLeave={e=>e.currentTarget.style.transform='scale(1)'}
                >
                  {em}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input bar */}
        <div style={{
          display:'flex', alignItems:'center', gap:8,
          padding:'10px 12px',
          background:`linear-gradient(180deg, transparent 0%, ${_T.bg}CC 100%)`,
          backdropFilter:'blur(16px)',
          borderTop:`1px solid ${_T.cardBorderBot}`,
          flexShrink:0,
        }}>
          {/* Emoji toggle */}
          <button
            onClick={() => { setShowEmoji(p => !p); setShowAttach(false); inputRef.current?.focus(); }}
            style={{ background: showEmoji ? `${accentHex}20` : 'none', border:`1px solid ${showEmoji ? accentHex+'44' : _T.cardBorderBot}`, borderRadius:14, fontSize:20, padding:'9px 11px', cursor:'pointer', flexShrink:0, transition:'all 0.2s', lineHeight:1 }}
          >
            😊
          </button>

          {/* Attach button (📎) — opens attach menu with photo/video/doc/audio */}
          <button
            onClick={() => { setShowAttach(p => !p); setShowEmoji(false); }}
            style={{ background: showAttach ? `${accentHex}20` : 'none', border:`1px solid ${showAttach ? accentHex+'44' : _T.cardBorderBot}`, borderRadius:14, fontSize:20, padding:'9px 11px', cursor:'pointer', flexShrink:0, transition:'all 0.2s', lineHeight:1, color: showAttach ? accentHex : C.silver }}
          >
            📎
          </button>

          {/* Hidden file inputs */}
          <input ref={fileRef}       type="file" accept="image/*"                                                                          style={{ display:'none' }} onChange={handleImageFile} />
          <input ref={videoFileRef}  type="file" accept="video/mp4,video/webm,video/quicktime,video/*"                                     style={{ display:'none' }} onChange={handleVideoFile} />
          <input ref={docFileRef}    type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,application/*,text/plain,text/csv" style={{ display:'none' }} onChange={handleDocFile} />
          <input ref={audioFileRef}  type="file" accept="audio/mpeg,audio/mp4,audio/ogg,audio/wav,audio/aac,audio/*"                       style={{ display:'none' }} onChange={handleAudioFileSelect} />

          {/* Text input — native OS keyboard */}
          <div style={{ flex:1, position:'relative' }}>
            <textarea
              ref={inputRef}
              value={input}
              onChange={onInputChange}
              onKeyDown={e => { if (e.key==='Enter' && !e.shiftKey) { e.preventDefault(); sendMsg(input); } }}
              placeholder={nl ? 'Schrijf een bericht...' : 'Write a message...'}
              rows={1}
              style={{
                width:'100%', boxSizing:'border-box',
                background:`linear-gradient(135deg, ${_T.bg3}, ${_T.bg4})`,
                border:`1px solid ${_T.cardBorderBot}`,
                borderRadius:18, color:'#EEF3FF', fontSize:14,
                padding:'11px 16px', resize:'none', outline:'none',
                lineHeight:1.5, fontFamily:'Inter,system-ui,sans-serif',
                maxHeight:120, overflowY:'auto',
                backdropFilter:'blur(10px)',
                boxShadow:`inset 0 1px 3px rgba(0,0,0,0.3)`,
                transition:'border-color 0.2s',
              }}
              onFocus={e => { e.target.style.borderColor = accentHex+'88'; setShowEmoji(false); setInputFocused(true); }}
              onBlur={e => { e.target.style.borderColor = _T.cardBorderBot; setInputFocused(false); }}
            />
          </div>

          {/* Floating balloon — pinned top of chat, shows typed text when keyboard is open */}
          {inputFocused && ReactDOM.createPortal(
            <div style={{
              position:'fixed',
              left:12, right:12,
              top:`calc(63px + env(safe-area-inset-top) + 10px)`,
              zIndex:1100,
              background:`linear-gradient(135deg, ${_T.bg3}F5, ${_T.bg4}F5)`,
              border:`1px solid ${accentHex}66`,
              borderRadius:18,
              padding:'10px 16px',
              backdropFilter:'blur(24px)',
              boxShadow:`0 4px 24px rgba(0,0,0,0.55), 0 0 0 1px ${accentHex}22`,
              fontSize:14,
              color: input ? '#EEF3FF' : C.silver,
              lineHeight:1.55,
              wordBreak:'break-word',
              whiteSpace:'pre-wrap',
              maxHeight:90,
              overflowY:'auto',
              pointerEvents:'none',
            }}>
              <span style={{ fontSize:10, color:accentHex, letterSpacing:1, fontWeight:700, display:'block', marginBottom:4 }}>
                ✏️ {nl ? 'Jouw bericht' : 'Your message'}
              </span>
              {input || (nl ? 'Begin te typen...' : 'Start typing...')}
            </div>,
            document.body
          )}

          {/* Send / Mic button */}
          {(input.trim() || imagePreview) ? (
            <button
              className={sendAnim ? 'send-burst' : ''}
              onClick={() => imagePreview ? sendImage() : sendMsg(input)}
              style={{
                background:`linear-gradient(135deg, ${accentHex} 0%, ${_T.grad2} 100%)`,
                border:`1px solid ${accentHex}88`,
                borderRadius:18, color:'#0a1220',
                fontSize:20, padding:'10px 14px', cursor:'pointer',
                flexShrink:0, lineHeight:1,
                boxShadow:`0 4px 16px ${accentHex}44`,
                transition:'all 0.2s',
              }}
            >
              ➤
            </button>
          ) : (
            <button
              onClick={isRecording ? undefined : startRecording}
              disabled={isRecording}
              style={{
                background: isRecording ? 'rgba(255,60,60,0.15)' : _T.bg3,
                border:`1px solid ${isRecording ? 'rgba(255,60,60,0.4)' : _T.cardBorderBot}`,
                borderRadius:18, color: isRecording ? '#ff6666' : C.silver,
                fontSize:20, padding:'10px 14px', cursor: isRecording ? 'default' : 'pointer',
                flexShrink:0, lineHeight:1, transition:'all 0.2s',
              }}
            >
              🎤
            </button>
          )}
        </div>

        {/* Image lightbox */}
        {lightboxImg && ReactDOM.createPortal(
          <div
            onClick={() => setLightboxImg(null)}
            style={{ position:'fixed', inset:0, zIndex:9999, background:'rgba(0,0,0,0.92)', display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}
          >
            <button
              onClick={() => setLightboxImg(null)}
              style={{ position:'absolute', top:20, right:20, background:'rgba(255,255,255,0.1)', border:'none', color:'#fff', fontSize:24, width:44, height:44, borderRadius:'50%', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}
            >✕</button>
            <img
              src={lightboxImg}
              alt=""
              onClick={e => e.stopPropagation()}
              style={{ maxWidth:'100%', maxHeight:'90vh', borderRadius:12, objectFit:'contain', boxShadow:'0 8px 48px rgba(0,0,0,0.8)' }}
            />
          </div>,
          document.body
        )}
      </div>,
      document.body
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // CONVERSATION LIST
  // ─────────────────────────────────────────────────────────────────────────
  const filteredFriends = friendSearch.trim()
    ? friendUsers.filter(u => (u.name||'').toLowerCase().includes(friendSearch.toLowerCase()))
    : friendUsers;

  return (
    <div>
      <style>{CHAT_CSS}</style>

      {/* Friend search */}
      {friendUsers.length > 0 && (
        <div style={{ position:'relative', marginBottom:12 }}>
          <span style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', fontSize:14, pointerEvents:'none', opacity:0.5 }}>🔍</span>
          <input
            type="text"
            value={friendSearch}
            onChange={e => setFriendSearch(e.target.value)}
            placeholder={nl ? 'Zoek vrienden...' : 'Search friends...'}
            style={{
              width:'100%', boxSizing:'border-box',
              background:_T.bg4, border:`1px solid ${_T.cardBorderBot}`,
              borderRadius:14, padding:'10px 14px 10px 36px',
              color:'#EEF3FF', fontSize:13, outline:'none',
              fontFamily:'Inter,system-ui,sans-serif',
            }}
          />
        </div>
      )}

      {friendUsers.length === 0 && (
        <div style={{
          textAlign:'center', padding:'40px 20px', borderRadius:20,
          background:`linear-gradient(135deg, ${_T.bg3}, ${_T.bg4})`,
          border:`1px solid ${_T.cardBorderBot}`,
          color:C.silver, fontSize:13,
        }}>
          <div style={{ fontSize:48, marginBottom:12 }}>💬</div>
          <div style={{ fontWeight:600, color:'#EEF3FF', marginBottom:4 }}>{nl ? 'Nog geen vrienden' : 'No friends yet'}</div>
          <div>{nl ? 'Voeg vrienden toe via het vriendentabblad.' : 'Add friends via the Friends tab.'}</div>
        </div>
      )}

      {friendSearch.trim() && filteredFriends.length === 0 && (
        <div style={{ textAlign:'center', color:C.silver, fontSize:13, padding:'20px 0' }}>
          {nl ? 'Geen vrienden gevonden.' : 'No friends found.'}
        </div>
      )}

      {filteredFriends.map(u => {
            const key = [currentUser.id, u.id].sort().join('_');
            const msgs = conversations[key] || [];
            const last = msgs[msgs.length - 1];
            const unread = msgs.filter(m => m.from !== currentUser.id && !m.read).length;
            const lastText = last?.type === 'image'     ? '📷 Photo'
                           : last?.type === 'voice'     ? '🎤 Voice message'
                           : last?.type === 'audiofile' ? '🎵 Audio'
                           : last?.type === 'video'     ? '🎬 Video'
                           : last?.type === 'document'  ? `📄 ${last?.filename || 'Document'}`
                           : last?.text;

            return (
              <div
                key={u.id}
                onClick={() => openChat(u)}
                style={{
                  display:'flex', alignItems:'center', gap:14, padding:'12px 14px', marginBottom:8,
                  background:`linear-gradient(135deg, ${_T.bg3} 0%, ${_T.bg4} 100%)`,
                  border:`1px solid ${unread > 0 ? _T.accent+'44' : _T.cardBorderBot}`,
                  borderRadius:18, cursor:'pointer',
                  boxShadow: unread > 0 ? `0 4px 20px ${_T.accent}22` : '0 2px 12px rgba(0,0,0,0.2)',
                  backdropFilter:'blur(10px)',
                  transition:'all 0.2s',
                }}
                onMouseEnter={e => e.currentTarget.style.transform='translateY(-2px) scale(1.01)'}
                onMouseLeave={e => e.currentTarget.style.transform='translateY(0) scale(1)'}
              >
                <div style={{ position:'relative', flexShrink:0 }}>
                  <Avatar user={u} size={48} profileImage={u.picture||null} />
                  <span style={{ position:'absolute', bottom:1, right:1 }}><StatusDot status={u.status||'offline'} size={12} /></span>
                </div>

                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:3 }}>
                    <div style={{ color:'#EEF3FF', fontSize:15, fontWeight:700 }}>{u.name}</div>
                    {unread > 0 && (
                      <div style={{ background:`linear-gradient(135deg,${_T.accent},${_T.grad2})`, color:'#0a1220', borderRadius:10, fontSize:10, fontWeight:800, padding:'1px 7px', animation:'pulse3d 1.5s infinite' }}>
                        {unread} new
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize:12, color: unread>0 ? C.silver2 : C.silver, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontWeight: unread>0 ? 500 : 400 }}>
                    {lastText || (nl ? '✉ Stuur een bericht' : '✉ Send a message')}
                  </div>
                </div>

                {last?.time && (
                  <div style={{ fontSize:10, color:C.silver, flexShrink:0 }}>{last.time}</div>
                )}
                <div style={{ fontSize:18, color:C.silver, flexShrink:0 }}>›</div>
              </div>
            );
          })}
    </div>
  );
}
